<template>
    <div v-if="applePayInstance" @click="buttonClicked" id="checkout__apple_pay-button" class="checkout__apple_pay-tab">
        <img src="https://cdn.iphonephotographyschool.com/cdn/sales/apple-pay-logo.jpg" alt="Apple Pay" />
        <portal to="checkout-full-overlay" v-if="paymentSheetActive && !isMobile">
            <div class="apple_pay_popup_active"></div>
        </portal>
    </div>
</template>

<script>
import braintree from 'braintree-web';
import { mapGetters, mapState } from 'vuex';
import axios from 'axios';
import msg from '../../../messages';

const APPLE_PAY_VERSION = 2;

export default {
    props: {
        braintreeClientInstance: {
            required: true,
        },
    },

    data() {
        return {
            applePayInstance: null,
            applePaySession: null,
            paymentSheetActive: false,
        };
    },

    mounted() {
        this.eventBus.on('CREATE_APPLE_PAY_SESSION', () => {
            this.createApplePaySession();
        });
        this.eventBus.on('MAKE_IMMEDIATE_APPLE_PAY_PAYMENT', () => {
            this.makePayment();
        });
    },

    computed: {
        ...mapGetters(['product', 'formData']),
        ...mapState(['applePayAvailable', 'lifetimeUpdates']),
        isPopupOpen() {
            return this.$store.state.popup.isOpen;
        },
        popupStep() {
            return this.$store.state.popup.step;
        },
        shouldTrigger() {
            return (
                this.applePayAvailable &&
                this.isPopupOpen &&
                this.popupStep === 2 &&
                this.$store.state.paymentType == 'now' &&
                /*
                 * Checkout popup should not trigger apple-pay payment sheet if default payment is apple-pay.
                 * It's triggered on the immediate payment button. Popup only shows up on error or cancel.
                 * If not - it's causing an error as that click is not user-triggered.
                 */
                this.$store.state.defaultPaymentMethod !== 'apple-pay'
            );
        },
    },

    watch: {
        braintreeClientInstance: 'setUp',
        isPopupOpen: 'triggerClick',
        popupStep: 'triggerClick',
    },

    methods: {
        setUp() {
            braintree.applePay
                .create({
                    client: this.braintreeClientInstance,
                })
                .then((applePayInstanceRaw) => {
                    ApplePaySession.canMakePaymentsWithActiveCard(applePayInstanceRaw.merchantIdentifier).then(
                        (canMakePaymentsWithActiveCard) => {
                            if (!canMakePaymentsWithActiveCard) {
                                return;
                            }

                            this.applePayInstance = applePayInstanceRaw;
                            this.$store.commit('applePayAvailable');
                            this.tracker.firePixelEvent('apple_pay_available');
                        },
                    );
                });
        },
        triggerClick() {
            if (this.shouldTrigger) {
                this.buttonClicked();
            }
        },
        buttonClicked() {
            this.makePayment();
        },
        createApplePaySession() {
            if (!this.applePayAvailable) {
                return;
            }

            const paymentRequest = this.applePayInstance.createPaymentRequest({
                total: {
                    label: `For ${this.product.title}`,
                    amount: this.lifetimeUpdates?.standalone
                        ? this.product.price.lu.value
                        : this.product.price.full.value,
                },
                currencyCode: this.product.price.full.currency,
            });

            this.applePaySession = new this.ApplePaySession(APPLE_PAY_VERSION, paymentRequest);

            this.applePaySession.oncancel = (event) => {
                this.paymentSheetActive = false;
                this.$store.commit('setProcessingCheckout', false);
                this.$store.commit('openCheckout', {
                    step: 2,
                });
                this.applePaySession = null;
            };

            this.applePaySession.onvalidatemerchant = (event) => {
                this.applePayInstance.performValidation(
                    {
                        validationURL: event.validationURL,
                        displayName: 'IPS Photography Courses',
                    },
                    (validationErr, merchantSession) => {
                        if (validationErr) {
                            this.paymentSheetActive = false;
                            this.applePaySession = null;
                            return;
                        }
                        this.applePaySession.completeMerchantValidation(merchantSession);
                    },
                );
            };

            this.applePaySession.onpaymentauthorized = (event) => {
                this.applePayInstance.tokenize(
                    {
                        token: event.payment.token,
                    },
                    async (err, payload) => {
                        if (err) {
                            this.paymentSheetActive = false;
                            this.$store.dispatch('showPaymentErrorModal', 'apple-pay');
                            logger.captureException(new Error(msg.checkout.applePayAuthorizationFailed), {
                                extra: { err },
                            });
                            this.applePaySession.completePayment(this.applePaySession.STATUS_FAILURE);
                            this.applePaySession = null;
                            return;
                        }

                        this.applePaySession.completePayment(this.applePaySession.STATUS_SUCCESS);
                        this.paymentSheetActive = false;

                        this.$store.commit('increaseCheckoutPaymentAttempts');

                        window.ipse.dispatch({
                            action: 'submit_payment_clicked',
                            data_event: {
                                type: 'apple-pay',
                            },
                        });

                        let response;

                        try {
                            response = await axios.post(
                                '/api/create-payment-method',
                                Object.assign({}, this.formData, { nonce: payload.nonce }),
                                { headers: { 'x-transaction-id': logger.getTransactionId() } },
                            );
                        } catch (err) {
                            window.ipse.dispatch({
                                action: 'create_payment_method_failed',
                                data_event: {
                                    purchase_flow: 'checkout',
                                    payment_method: 'apple-pay',
                                    error: err,
                                },
                            });

                            this.paymentSheetActive = false;
                            this.$store.dispatch('showPaymentErrorModal', 'apple-pay');
                            logger.captureException(new Error(msg.checkout.applePayPaymentMethodFailed), {
                                extra: err,
                            });
                            return;
                        }

                        logger.addCheckoutBreadcrumb({ message: msg.checkout.applePayPaymentMethodCreated });
                        this.$store.commit('setLastCheckoutPaymentMethod', 'apple-pay');
                        this.$store.commit('setPaymentMethod', {
                            paymentMethodToken: response.data.token,
                        });
                        this.$emit('received-token', response.data.token);
                    },
                );
            };
        },
        makePayment() {
            if (!this.applePaySession) {
                this.createApplePaySession();
            }

            this.tracker.fireAddPaymentInfoEvent('Apple Pay');
            this.paymentSheetActive = true;
            this.applePaySession.begin();
        },
    },
};
</script>
